'use client';

import { useState, useMemo } from 'react';
import { Search, Plus, Eye, Edit2, ClipboardList, Calendar, User, Trash2 } from 'lucide-react';
import { DataTable } from '../../../components/shared/DataTable';
import { PageHeader } from '../../../components/shared/PageHeader';
import { Modal, FormField, FormInput, FormSelect, FormTextarea, Button } from '../../../components/shared/Modal';
import { materialRequests } from '../data/mock-data';
import {
  materialRequestStatusConfig,
  materialRequestTypeConfig,
  type MaterialRequestStatus,
  type MaterialRequestType,
} from '../types';
import type { MaterialRequest } from '../types';

// 자재요청 등록 모달
function MaterialRequestAddModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const [items, setItems] = useState([{ itemName: '', quantity: 1 }]);

  const addItem = () => setItems([...items, { itemName: '', quantity: 1 }]);
  const removeItem = (index: number) => {
    if (items.length > 1) setItems(items.filter((_, i) => i !== index));
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="자재요청 등록"
      description="새로운 자재요청을 등록합니다."
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>취소</Button>
          <Button onClick={onClose}>요청 등록</Button>
        </>
      }
    >
      <div className="space-y-6">
        <div className="grid grid-cols-2 gap-4">
          <FormField label="요청번호" required>
            <FormInput placeholder="자동 생성" disabled />
          </FormField>
          <FormField label="요청 유형" required>
            <FormSelect>
              <option value="">선택하세요</option>
              {Object.entries(materialRequestTypeConfig).map(([type, config]) => (
                <option key={type} value={type}>{config.label}</option>
              ))}
            </FormSelect>
          </FormField>
          <FormField label="요청자" required>
            <FormInput placeholder="요청자명" />
          </FormField>
          <FormField label="부서">
            <FormSelect>
              <option value="">선택하세요</option>
              <option value="production">생산부</option>
              <option value="warehouse">창고팀</option>
              <option value="quality">품질관리팀</option>
            </FormSelect>
          </FormField>
          <FormField label="필요일" required>
            <FormInput type="date" />
          </FormField>
          <FormField label="우선순위">
            <FormSelect>
              <option value="low">낮음</option>
              <option value="medium">보통</option>
              <option value="high">높음</option>
              <option value="urgent">긴급</option>
            </FormSelect>
          </FormField>
        </div>

        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-medium">요청 품목</h3>
            <Button variant="outline" size="sm" onClick={addItem}>
              <Plus className="w-4 h-4 mr-1" />품목 추가
            </Button>
          </div>
          <div className="border border-border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left p-3 font-medium">품목</th>
                  <th className="text-right p-3 font-medium w-32">요청수량</th>
                  <th className="w-12"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((_, index) => (
                  <tr key={index} className="border-t border-border">
                    <td className="p-2">
                      <FormSelect>
                        <option value="">품목 선택</option>
                        <option value="mat-001">원지 (A등급)</option>
                        <option value="mat-002">원지 (B등급)</option>
                        <option value="mat-003">골심지</option>
                        <option value="mat-004">접착제</option>
                      </FormSelect>
                    </td>
                    <td className="p-2"><FormInput type="number" min={1} defaultValue={1} className="text-right" /></td>
                    <td className="p-2">
                      <button onClick={() => removeItem(index)} className="p-1 rounded hover:bg-accent" disabled={items.length === 1}>
                        <Trash2 className="w-4 h-4 text-muted-foreground" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <FormField label="요청 사유">
          <FormTextarea rows={2} placeholder="자재 요청 사유를 입력하세요" />
        </FormField>
      </div>
    </Modal>
  );
}

export function MaterialRequestListPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [selectedType, setSelectedType] = useState<string>('all');
  const [isModalOpen, setIsModalOpen] = useState(false);

  const filteredData = useMemo(() => {
    return materialRequests.filter((request) => {
      // 검색 필터
      const matchesSearch =
        request.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
        request.requestedBy.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (request.department && request.department.toLowerCase().includes(searchQuery.toLowerCase()));

      // 상태 필터
      const matchesStatus =
        selectedStatus === 'all' || request.status === selectedStatus;

      // 유형 필터
      const matchesType =
        selectedType === 'all' || request.type === selectedType;

      return matchesSearch && matchesStatus && matchesType;
    });
  }, [searchQuery, selectedStatus, selectedType]);

  const columns = [
    {
      key: 'code',
      label: '요청번호',
      width: '11%',
      sortable: true,
      render: (value: unknown) => (
        <span className="font-mono text-sm text-primary">{String(value)}</span>
      ),
    },
    {
      key: 'type',
      label: '유형',
      width: '8%',
      render: (value: unknown) => {
        const type = value as MaterialRequestType;
        const config = materialRequestTypeConfig[type];
        const colorClasses: Record<string, string> = {
          blue: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
          purple: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
          orange: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
          green: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
        };
        return (
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${colorClasses[config.color]}`}>
            {config.label}
          </span>
        );
      },
    },
    {
      key: 'requestedBy',
      label: '요청자',
      width: '14%',
      render: (value: unknown, row: MaterialRequest) => (
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center flex-shrink-0">
            <User className="w-4 h-4 text-orange-600 dark:text-orange-400" />
          </div>
          <div className="min-w-0">
            <div className="font-medium truncate">{String(value)}</div>
            {row.department && (
              <div className="text-xs text-muted-foreground truncate">
                {row.department}
              </div>
            )}
          </div>
        </div>
      ),
    },
    {
      key: 'requestDate',
      label: '요청일',
      width: '12%',
      sortable: true,
      render: (value: unknown, row: MaterialRequest) => (
        <div>
          <div className="flex items-center gap-1 text-sm">
            <Calendar className="w-3 h-3 text-muted-foreground flex-shrink-0" />
            <span className="whitespace-nowrap">{String(value)}</span>
          </div>
          <div className="text-xs text-muted-foreground whitespace-nowrap">
            필요일: {row.requiredDate}
          </div>
        </div>
      ),
    },
    {
      key: 'totalQty',
      label: '품목/수량',
      width: '10%',
      render: (value: unknown, row: MaterialRequest) => (
        <div>
          <div className="font-medium whitespace-nowrap">{row.items.length}개 품목</div>
          <div className="text-xs text-muted-foreground whitespace-nowrap">
            총 {String(value)}
          </div>
        </div>
      ),
    },
    {
      key: 'progress',
      label: '진행상황',
      width: '16%',
      render: (_: unknown, row: MaterialRequest) => {
        const totalQty = row.items.reduce((sum, item) => sum + item.quantity, 0);
        const orderedQty = row.items.reduce((sum, item) => sum + item.orderedQty, 0);
        const receivedQty = row.items.reduce((sum, item) => sum + item.receivedQty, 0);
        const orderedPercent = totalQty > 0 ? Math.round((orderedQty / totalQty) * 100) : 0;
        const receivedPercent = totalQty > 0 ? Math.round((receivedQty / totalQty) * 100) : 0;

        return (
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <div className="flex-1 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-blue-500"
                  style={{ width: `${orderedPercent}%` }}
                />
              </div>
              <span className="text-xs text-muted-foreground whitespace-nowrap">
                {orderedPercent}%
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex-1 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-green-500"
                  style={{ width: `${receivedPercent}%` }}
                />
              </div>
              <span className="text-xs text-muted-foreground whitespace-nowrap">
                {receivedPercent}%
              </span>
            </div>
          </div>
        );
      },
    },
    {
      key: 'status',
      label: '상태',
      width: '8%',
      render: (value: unknown) => {
        const status = value as MaterialRequestStatus;
        const config = materialRequestStatusConfig[status];
        const colorClasses: Record<string, string> = {
          gray: 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400',
          blue: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
          yellow: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
          orange: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
          cyan: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400',
          green: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
          red: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
        };
        return (
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${colorClasses[config.color]}`}>
            {config.label}
          </span>
        );
      },
    },
    {
      key: 'actions',
      label: '',
      width: '7%',
      render: () => (
        <div className="flex items-center gap-1">
          <button className="p-1.5 rounded hover:bg-accent" title="보기">
            <Eye className="w-4 h-4 text-muted-foreground" />
          </button>
          <button className="p-1.5 rounded hover:bg-accent" title="수정">
            <Edit2 className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>
      ),
    },
  ];

  // 상태별 통계
  const statusStats = useMemo(() => {
    const stats: Record<string, number> = {};
    materialRequests.forEach((request) => {
      stats[request.status] = (stats[request.status] || 0) + 1;
    });
    return stats;
  }, []);

  // 유형별 통계
  const typeStats = useMemo(() => {
    const stats: Record<string, number> = {};
    materialRequests.forEach((request) => {
      stats[request.type] = (stats[request.type] || 0) + 1;
    });
    return stats;
  }, []);

  // 요약 정보
  const totalRequests = materialRequests.length;
  const pendingRequests = materialRequests.filter(
    (r) => r.status === 'pending' || r.status === 'submitted'
  ).length;

  return (
    <div className="space-y-6">
      <PageHeader
        title="자재요청 관리"
        description="자재요청을 조회하고 관리합니다."
        actions={
          <button
            onClick={() => setIsModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90"
          >
            <Plus className="w-4 h-4" />
            자재요청 등록
          </button>
        }
      />

      {/* 유형별 요약 카드 */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {Object.entries(materialRequestTypeConfig).map(([type, config]) => {
          const colorClasses: Record<string, string> = {
            blue: 'border-blue-200 dark:border-blue-800',
            purple: 'border-purple-200 dark:border-purple-800',
            orange: 'border-orange-200 dark:border-orange-800',
            green: 'border-green-200 dark:border-green-800',
          };
          return (
            <button
              key={type}
              onClick={() => setSelectedType(selectedType === type ? 'all' : type)}
              className={`p-3 rounded-lg border-2 text-left transition-colors ${
                selectedType === type
                  ? `${colorClasses[config.color]} bg-primary/5`
                  : 'border-border bg-card hover:bg-accent/50'
              }`}
            >
              <div className="text-xs text-muted-foreground">{config.label}</div>
              <div className="text-xl font-bold mt-1">
                {typeStats[type] || 0}건
              </div>
            </button>
          );
        })}
      </div>

      {/* 요약 정보 */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="p-4 rounded-lg bg-card border border-border">
          <div className="text-sm text-muted-foreground">전체 요청</div>
          <div className="text-2xl font-bold mt-1">{totalRequests}건</div>
        </div>
        <div className="p-4 rounded-lg bg-card border border-border">
          <div className="text-sm text-muted-foreground">처리 대기</div>
          <div className="text-2xl font-bold mt-1 text-yellow-500">
            {pendingRequests}건
          </div>
        </div>
      </div>

      {/* 필터 영역 */}
      <div className="flex flex-wrap gap-4">
        {/* 검색 */}
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="요청번호, 요청자, 부서 검색..."
            className="w-full h-10 pl-10 pr-4 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        {/* 상태 필터 */}
        <select
          value={selectedStatus}
          onChange={(e) => setSelectedStatus(e.target.value)}
          className="h-10 px-3 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <option value="all">전체 상태</option>
          {Object.entries(materialRequestStatusConfig).map(([status, config]) => (
            <option key={status} value={status}>
              {config.label} ({statusStats[status] || 0})
            </option>
          ))}
        </select>

        {/* 유형 필터 */}
        <select
          value={selectedType}
          onChange={(e) => setSelectedType(e.target.value)}
          className="h-10 px-3 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <option value="all">전체 유형</option>
          {Object.entries(materialRequestTypeConfig).map(([type, config]) => (
            <option key={type} value={type}>
              {config.label}
            </option>
          ))}
        </select>
      </div>

      {/* 데이터 테이블 */}
      <DataTable
        data={filteredData}
        columns={columns}
        pageSize={10}
        emptyMessage="검색 결과가 없습니다."
      />

      {/* 자재요청 등록 모달 */}
      <MaterialRequestAddModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
}
